import { type RefObject, useEffect, useRef, useState } from 'react';

// Vega re-lays out the view on every reported width, so a coarse step keeps a
// panel drag cheap. The SVG scales itself between steps via its viewBox.
const WIDTH_STEP_PX = 16;
// Keep charts just outside the viewport current, so scrolling reveals a
// correctly sized chart instead of one that resizes on arrival.
const VISIBILITY_MARGIN_PX = 200;

function snapWidth(width: number): number {
  return Math.round(width / WIDTH_STEP_PX) * WIDTH_STEP_PX;
}

/**
 * Charts live inside an overflow scroller (the assistant messages list, the
 * page-sidebar chat). IntersectionObserver's default root is the viewport, so
 * `rootMargin` would expand the window instead of the conversation and miss
 * charts clipped by that scroller.
 */
function nearestScrollRoot(element: Element): Element | null {
  let node = element.parentElement;
  while (node != null) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Reports the host's width in steps, and only while the chart is near the
 * viewport. Charts scrolled out of a long conversation stay idle during a
 * panel drag and catch up when they scroll back in.
 */
export function useChatChartWidth(
  hostRef: RefObject<HTMLElement | null>
): number {
  const [width, setWidth] = useState(0);
  const pendingRef = useRef(0);
  const nearViewportRef = useRef(true);

  useEffect(() => {
    const host = hostRef.current;
    if (host == null || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const next = snapWidth(entries[0]?.contentRect.width ?? 0);
      if (next <= 0) {
        return;
      }

      pendingRef.current = next;
      if (nearViewportRef.current) {
        setWidth((current) => (current === next ? current : next));
      }
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, [hostRef]);

  useEffect(() => {
    const host = hostRef.current;
    if (host == null || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const nearViewport = entries.at(-1)?.isIntersecting ?? true;
        nearViewportRef.current = nearViewport;
        if (!nearViewport || pendingRef.current <= 0) {
          return;
        }

        setWidth((current) =>
          current === pendingRef.current ? current : pendingRef.current
        );
      },
      {
        root: nearestScrollRoot(host),
        rootMargin: `${VISIBILITY_MARGIN_PX}px`,
      }
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, [hostRef]);

  return width;
}
